import { AuthService } from '@/core/auth/services/AuthService.ts'
import { type IAppLifecycleService } from '@/core/lifecycle/AppLifecycleService'
import { GqlClientService } from '@/common/network/gql-client-service'
import { SpaceGqlModel } from '../gql/SpaceGqlModel'
import type { GqlResponse } from '@/common/network/gql-types'
import type { OperationResult } from '@/common/errors/OperationResult'
import * as op from '../../common/errors/OperationResult'
import { BehaviorSubject } from 'rxjs'
import type { IBehaviorObservable } from '@/common/react/IBehaviorObservable'
import { NumberLocalStorageValue } from '@/common/local-storage/NumberLocalStorageValue'
import type { DeleteSpaceInput } from '../gql/DeleteSpaceInput'
import { assertLog, getTimeStr } from '@/common/errors/log-utils'
import { SpacesApiService } from './SpacesApiService'

type GetSpacesResponse = { spaces: SpaceGqlModel[] }

export interface ISpacesService {
  selectedSpace$: IBehaviorObservable<SpaceGqlModel | undefined>
  spaces$: IBehaviorObservable<SpaceGqlModel[] | undefined>
  setSelectedSpace(space: SpaceGqlModel): void

  createSpace(spaceName: string): Promise<OperationResult<SpaceGqlModel>>
  deleteSpace(id: number): Promise<OperationResult>
}

export class SpacesService implements ISpacesService {
  public static readonly selectedSpaceIdLocalStorageKey = 'selectedSpaceId'

  public selectedSpace$: IBehaviorObservable<SpaceGqlModel | undefined>
  public spaces$: IBehaviorObservable<SpaceGqlModel[] | undefined>
  private readonly selectedSpace: BehaviorSubject<SpaceGqlModel | undefined>
  private readonly spaces: BehaviorSubject<SpaceGqlModel[] | undefined>
  private readonly selectedSpaceIdLsValue = new NumberLocalStorageValue(
    SpacesService.selectedSpaceIdLocalStorageKey,
  )
  private fetchAbortController?: AbortController

  constructor(
    private readonly authService: AuthService,
    private readonly gqlClientService: GqlClientService,
    private readonly spacesApiService: SpacesApiService,
    private readonly appLifecycleService: IAppLifecycleService,
  ) {
    this.selectedSpace = new BehaviorSubject<SpaceGqlModel | undefined>(undefined)
    this.selectedSpace$ = this.selectedSpace
    this.spaces = new BehaviorSubject<SpaceGqlModel[] | undefined>(undefined)
    this.spaces$ = this.spaces
    void this.initAsync()
  }

  public async createSpace(spaceName: string): Promise<OperationResult<SpaceGqlModel>> {
    const { error, result: createdSpace } = await this.spacesApiService.createSpace(spaceName)

    if (error) {
      return op.error(error)
    }

    const spaces = [...this.spaces.value!, createdSpace!]
    this.spaces.next(spaces)
    return op.success(createdSpace!)
  }

  public async deleteSpace(id: number): Promise<OperationResult> {
    const mutation = `mutation($input: DeleteSpaceInput!) {
  deleteSpace(input: $input)
}`
    const { error } = await this.gqlClientService.sendGqlRequest<GqlResponse<unknown>>(mutation, {
      variables: {
        input: { id } as DeleteSpaceInput,
      },
    })

    if (error) {
      return op.error(error)
    }

    const predicate = (x: SpaceGqlModel) => x.id == id
    const spaces = this.spaces.value!.filter((x) => !predicate(x))
    assertLog(spaces.length == this.spaces.value!.length + 1, 'spaces need to be -1 after delete')

    this.spaces.next(spaces)
    return op.success()
  }

  public setSelectedSpace(space: SpaceGqlModel): void {
    this.selectedSpace.next(space)
    this.selectedSpaceIdLsValue.value = space.id
  }

  private async initAsync(): Promise<void> {
    if (this.authService.getAuthToken()) {
      await this.fetchSpaces()
    } else {
      if (await this.authService.refresh()) {
        await this.fetchSpaces()
      }
    }
    this.authService.onAuthStatusChange$.subscribe(this.onAuthStatusChanged.bind(this))
  }

  private onAuthStatusChanged(isAuthed: boolean) {
    if (!isAuthed) {
      return
    }
    void this.fetchSpaces()
  }

  private async fetchSpaces(): Promise<void> {
    this.fetchAbortController?.abort()

    const query = `
    query {
      spaces {
        id
        name
      }
    }
  `

    this.fetchAbortController = new AbortController()
    const reqResult = await this.gqlClientService.sendGqlRequest<GetSpacesResponse>(query, {
      logError: false,
    })
    if (reqResult.isError()) {
      console.error(`error fetching spaces: ${reqResult.error.message}`)
      return
    }

    const spaces = reqResult.result!.data!.spaces
    if (!this.selectedSpaceIdLsValue.wasRead) {
      this.selectedSpaceIdLsValue.read()
    }
    let selectedSpace = spaces.find(({ id }) => id === this.selectedSpaceIdLsValue.value)!
    if (!selectedSpace) {
      selectedSpace = spaces[0]
      this.selectedSpaceIdLsValue.value = selectedSpace.id
    }

    console.log(`spaces fetched: ${spaces.length}, time: ${getTimeStr()}`)
    this.spaces.next(spaces)
    this.selectedSpace.next(selectedSpace)
    this.authService.notifyAuthSuccess()
  }
}
